import { Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useActiveTeam } from "@/hooks/useActiveTeam";

interface TeamFilterDropdownProps {
  value: string | null;
  onChange: (teamId: string | null) => void;
  className?: string;
  allowAll?: boolean;
}

/**
 * Reusable team filter dropdown for list pages.
 * Only renders if the user belongs to more than one team.
 * Operative list views default to the active team and hide the "All Teams" option.
 */
// TES-1664: pre-existing filter branching; this PR only adds an accessible name.
// fallow-ignore-next-line complexity
export function TeamFilterDropdown({
  value,
  onChange,
  className,
  allowAll = false,
}: TeamFilterDropdownProps) {
  const { t } = useTranslation();
  const { activeTeamId, userTeams, isMultiTeam, isLoading } = useActiveTeam();

  // Don't render for single-team or no-team users
  if (isLoading || !isMultiTeam) return null;

  return (
    <Select
      value={value ?? (allowAll ? "all" : (activeTeamId ?? ""))}
      onValueChange={(v) => onChange(v === "all" ? null : v)}
    >
      <SelectTrigger
        className={className ?? "w-[160px] text-xs md:text-sm"}
        data-testid="team-filter-dropdown"
        aria-label={t("teams.teamFilter")}
      >
        <Users className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 shrink-0" />
        <SelectValue placeholder={t("teams.teamFilter")} />
      </SelectTrigger>
      <SelectContent>
        {allowAll && <SelectItem value="all">{t("teams.allTeams")}</SelectItem>}
        {userTeams.map((team) => (
          <SelectItem key={team.id} value={team.id}>
            {team.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
